import React, { useCallback, useRef } from 'react'
import { BaseDragHandle } from '@fs/zion-ui/quarks-authorized-use-only'
import { colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { getRotationMatrix, rotatePointWithMatrix } from './zoomPanDraggableUtils'

const HANDLE_SIZE = 24
const CONTAINER_MIN_SIZE = 24

const baseDragHandleCss = css`
  block-size: ${HANDLE_SIZE}px;
  border-radius: 4px;
  inline-size: ${HANDLE_SIZE}px;
  outline: none;
  position: absolute;

  ::after {
    background: ${colors.gray00};
    block-size: 100%;
    border: 2px solid ${colors.blue40};
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    content: '';
    display: block;
    inline-size: 100%;
  }

  &:focus-visible {
    outline: 4px solid ${colors.blue40};
  }
`

function getRotatedResizeCursor(rotAngle, isBottomRight, isBottomLeft, isTopRight, isTopLeft) {
  let baseAngle = 0
  if (isBottomRight) baseAngle = 45
  else if (isTopLeft) baseAngle = 225
  else if (isBottomLeft) baseAngle = 135
  else if (isTopRight) baseAngle = 315

  const angle = (baseAngle + rotAngle) % 360

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
      <g transform="rotate(${angle},12,12)">
        <polyline
          points="1,12 4,9 4,12 20,12 20,9 23,12 20,15 20,12 4,12 4,15 1,12"
          fill="black"
          stroke="white"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <polyline
          points="1,12 4,9 4,12 20,12 20,9 23,12 20,15 20,12 4,12 4,15 1,12"
          fill="black"
          stroke="black"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </svg>
    `
}

export function AspectRatioCornerResizeHandles({
  testIdPrefix,
  getBounds,
  maxSize,
  minSize,
  defaultSize,
  ...restProps
}) {
  const sharedProps = {
    getBounds,
    maxSize,
    minSize,
    defaultSize,
    ...restProps,
  }

  return (
    <>
      <AspectRatioResizeHandle
        {...sharedProps}
        bottomRight
        data-testid={testIdPrefix ? `${testIdPrefix}-bottom-right` : undefined}
      />
      <AspectRatioResizeHandle
        {...sharedProps}
        bottomLeft
        data-testid={testIdPrefix ? `${testIdPrefix}-bottom-left` : undefined}
      />
      <AspectRatioResizeHandle
        {...sharedProps}
        data-testid={testIdPrefix ? `${testIdPrefix}-top-right` : undefined}
        topRight
      />
      <AspectRatioResizeHandle
        {...sharedProps}
        data-testid={testIdPrefix ? `${testIdPrefix}-top-left` : undefined}
        topLeft
      />
    </>
  )
}

export function AspectRatioBottomLeftResizeHandle({
  testIdPrefix,
  getBounds,
  maxSize,
  minSize,
  defaultSize,
  ...restProps
}) {
  return (
    <AspectRatioResizeHandle
      data-testid={testIdPrefix ? `${testIdPrefix}-bottom-left` : undefined}
      getBounds={getBounds}
      maxSize={maxSize}
      minSize={minSize}
      defaultSize={defaultSize}
      {...restProps}
      bottomLeft
    />
  )
}

export function AspectRatioBottomRightResizeHandle({
  testIdPrefix,
  getBounds,
  maxSize,
  minSize,
  defaultSize,
  ...restProps
}) {
  return (
    <AspectRatioResizeHandle
      data-testid={testIdPrefix ? `${testIdPrefix}-bottom-right` : undefined}
      getBounds={getBounds}
      maxSize={maxSize}
      minSize={minSize}
      defaultSize={defaultSize}
      {...restProps}
      bottomRight
    />
  )
}

export function AspectRatioTopLeftResizeHandle({
  testIdPrefix,
  getBounds,
  maxSize,
  minSize,
  defaultSize,
  ...restProps
}) {
  return (
    <AspectRatioResizeHandle
      data-testid={testIdPrefix ? `${testIdPrefix}-top-left` : undefined}
      getBounds={getBounds}
      maxSize={maxSize}
      minSize={minSize}
      defaultSize={defaultSize}
      {...restProps}
      topLeft
    />
  )
}

export function AspectRatioTopRightResizeHandle({
  testIdPrefix,
  getBounds,
  maxSize,
  minSize,
  defaultSize,
  ...restProps
}) {
  return (
    <AspectRatioResizeHandle
      data-testid={testIdPrefix ? `${testIdPrefix}-top-right` : undefined}
      getBounds={getBounds}
      maxSize={maxSize}
      minSize={minSize}
      defaultSize={defaultSize}
      {...restProps}
      topRight
    />
  )
}

export default function AspectRatioResizeHandle({
  aspectRatio,
  bottomLeft,
  bottomRight,
  getBounds: containerGetBounds,
  maxSize, // Note: If `maxSize` is not specified it will be the size of the ZoomPan Stage area.
  minSize = CONTAINER_MIN_SIZE,
  topLeft,
  topRight,
  rotation = 0,
  value: currentSizePercentage,
  defaultSize,
  ...props
}) {
  const [t] = useTranslation()
  const percentageValue = Number.isFinite(currentSizePercentage) ? currentSizePercentage : 0
  if (!bottomLeft && !bottomRight && !topLeft && !topRight) {
    bottomRight = true
  }

  // Store aspect ratio once (either provided or lazily derived on first drag frame)
  const aspectRatioRef = useRef(aspectRatio || null)

  const calculateNewDimensions = useCallback(({ currentRect, delta, handleType, ratio }) => {
    const { x, y, width, height } = currentRect
    let newWidth = width
    let newHeight = height
    let newX = x
    let newY = y

    if (handleType === 'bottomRight') {
      const wDelta = delta.x
      const hDelta = delta.y
      if (Math.abs(wDelta) > Math.abs(hDelta)) {
        newWidth = width + wDelta
        newHeight = newWidth / ratio
      } else {
        newHeight = height + hDelta
        newWidth = newHeight * ratio
      }
    } else if (handleType === 'bottomLeft') {
      const wDelta = -delta.x
      const hDelta = delta.y
      if (Math.abs(wDelta) > Math.abs(hDelta)) {
        newWidth = width + wDelta
        newHeight = newWidth / ratio
      } else {
        newHeight = height + hDelta
        newWidth = newHeight * ratio
      }
      newX = x + width - newWidth
    } else if (handleType === 'topRight') {
      const wDelta = delta.x
      const hDelta = -delta.y
      if (Math.abs(wDelta) > Math.abs(hDelta)) {
        newWidth = width + wDelta
        newHeight = newWidth / ratio
      } else {
        newHeight = height + hDelta
        newWidth = newHeight * ratio
      }
      newY = y + height - newHeight
    } else if (handleType === 'topLeft') {
      const wDelta = -delta.x
      const hDelta = -delta.y
      if (Math.abs(wDelta) > Math.abs(hDelta)) {
        newWidth = width + wDelta
        newHeight = newWidth / ratio
      } else {
        newHeight = height + hDelta
        newWidth = newHeight * ratio
      }
      newX = x + width - newWidth
      newY = y + height - newHeight
    }

    return { newWidth, newHeight, newX, newY }
  }, [])

  const applySizeConstraints = useCallback(
    ({ dimensions, ratio }) => {
      let { newWidth, newHeight } = dimensions

      // Apply minimum size constraint
      if (newWidth < minSize) {
        newWidth = minSize
        newHeight = newWidth / ratio
      }
      if (newHeight < minSize) {
        newHeight = minSize
        newWidth = newHeight * ratio
      }

      // Apply max size constraints
      if (maxSize) {
        if (newWidth > maxSize) {
          newWidth = maxSize
          newHeight = newWidth / ratio
        }
        if (newHeight > maxSize) {
          newHeight = maxSize
          newWidth = newHeight * ratio
        }
      }

      return { ...dimensions, newWidth, newHeight }
    },
    [maxSize, minSize]
  )

  const applyBoundaryConstraints = useCallback(
    ({ boundary, dimensions, ratio }) => {
      if (!boundary) return dimensions

      let { newWidth, newHeight, newX, newY } = dimensions
      const boundaryRight = boundary.x + boundary.width
      const boundaryBottom = boundary.y + boundary.height

      // Constrain to fit within boundary
      const leftConstraintNeeded = newX < boundary.x
      const topConstraintNeeded = newY < boundary.y

      if (leftConstraintNeeded && topConstraintNeeded) {
        // Both constraints needed - apply the more restrictive one
        const leftDiff = boundary.x - newX
        const topDiff = boundary.y - newY

        if (leftDiff >= topDiff) {
          // Left constraint is more restrictive
          newX = boundary.x
          newWidth -= leftDiff
          newHeight = newWidth / ratio
        } else {
          // Top constraint is more restrictive
          newY = boundary.y
          newHeight -= topDiff
          newWidth = newHeight * ratio
        }
      } else if (leftConstraintNeeded) {
        const diff = boundary.x - newX
        newX = boundary.x
        newWidth -= diff
        newHeight = newWidth / ratio
      } else if (topConstraintNeeded) {
        const diff = boundary.y - newY
        newY = boundary.y
        newHeight -= diff
        newWidth = newHeight * ratio
      }

      if (newX + newWidth > boundaryRight) {
        newWidth = boundaryRight - newX
        newHeight = newWidth / ratio
      }
      if (newY + newHeight > boundaryBottom) {
        newHeight = boundaryBottom - newY
        newWidth = newHeight * ratio
      }

      // Re-apply minimum size after boundary constraints
      if (newWidth < minSize) {
        newWidth = minSize
        newHeight = newWidth / ratio
      }
      if (newHeight < minSize) {
        newHeight = minSize
        newWidth = newHeight * ratio
      }

      return { newWidth, newHeight, newX, newY }
    },
    [minSize]
  )

  const getHandleType = useCallback(() => {
    if (bottomRight) return 'bottomRight'
    if (bottomLeft) return 'bottomLeft'
    if (topRight) return 'topRight'
    if (topLeft) return 'topLeft'
    return 'bottomRight'
  }, [bottomRight, bottomLeft, topRight, topLeft])

  const getHandleLabel = useCallback(() => {
    if (bottomRight)
      return t('AspectRatioResizeHandle.keyboardDescription.bottomRight', 'Bottom Right Resize Sticker Handle')
    if (bottomLeft)
      return t('AspectRatioResizeHandle.keyboardDescription.bottomLeft', 'Bottom Left Resize Sticker Handle')
    if (topRight) return t('AspectRatioResizeHandle.keyboardDescription.topRight', 'Top Right Resize Sticker Handle')
    if (topLeft) return t('AspectRatioResizeHandle.keyboardDescription.topLeft', 'Top Left Resize Sticker Handle')
    return t('AspectRatioResizeHandle.keyboardDescription.bottomRight', 'Bottom Right Resize Sticker Handle')
  }, [bottomRight, t, bottomLeft, topRight, topLeft])

  const handleResize = useCallback(
    ({ currentRect, delta }) => {
      const { x, y, width, height } = currentRect
      const rotationMatrix = getRotationMatrix(rotation)
      const inverseRotationMatrix = getRotationMatrix(-rotation)

      // Transform delta into local sticker axes using negative rotation
      const localDelta = rotatePointWithMatrix(delta, inverseRotationMatrix)

      // Identify anchor (opposite) corner in local space
      let anchorLocal = { x: 0, y: 0 }
      if (bottomRight) {
        anchorLocal = { x, y }
      } else if (bottomLeft) {
        anchorLocal = { x: x + width, y }
      } else if (topRight) {
        anchorLocal = { x, y: y + height }
      } else if (topLeft) {
        anchorLocal = { x: x + width, y: y + height }
      }

      // Transform anchor to global space
      const cx = x + width / 2
      const cy = y + height / 2
      const anchorVec = { x: anchorLocal.x - cx, y: anchorLocal.y - cy }
      const rotatedAnchorVec = rotatePointWithMatrix(anchorVec, rotationMatrix)
      const anchorGlobal = {
        x: cx + rotatedAnchorVec.x,
        y: cy + rotatedAnchorVec.y,
      }

      // Get boundary
      const containerBounds = containerGetBounds()
      const containerBoundary = containerBounds?.boundary

      // Get or calculate aspect ratio
      if (!aspectRatioRef.current) {
        aspectRatioRef.current = width > 0 && height > 0 ? width / height : 1
      }
      const ratio = aspectRatioRef.current || 1

      // Calculate new dimensions using localDelta
      const handleType = getHandleType()
      let dimensions = calculateNewDimensions({ currentRect, delta: localDelta, handleType, ratio })

      // Apply constraints
      dimensions = applySizeConstraints({ dimensions, ratio })
      dimensions = applyBoundaryConstraints({ boundary: containerBoundary, dimensions, ratio })

      // Compute anchor offset in new local space
      let newAnchorOffset = { x: 0, y: 0 }
      if (bottomRight) {
        newAnchorOffset = { x: -dimensions.newWidth / 2, y: -dimensions.newHeight / 2 }
      } else if (bottomLeft) {
        newAnchorOffset = { x: dimensions.newWidth / 2, y: -dimensions.newHeight / 2 }
      } else if (topRight) {
        newAnchorOffset = { x: -dimensions.newWidth / 2, y: dimensions.newHeight / 2 }
      } else if (topLeft) {
        newAnchorOffset = { x: dimensions.newWidth / 2, y: dimensions.newHeight / 2 }
      }

      // Rotate new anchor offset by rotation
      const newAnchorOffsetRot = rotatePointWithMatrix(newAnchorOffset, rotationMatrix)

      // Set new center so anchor stays at anchorGlobal
      const finalCx = anchorGlobal.x - newAnchorOffsetRot.x
      const finalCy = anchorGlobal.y - newAnchorOffsetRot.y
      const finalX = finalCx - dimensions.newWidth / 2
      const finalY = finalCy - dimensions.newHeight / 2

      return {
        height: dimensions.newHeight,
        width: dimensions.newWidth,
        x: finalX,
        y: finalY,
      }
    },
    [
      bottomLeft,
      bottomRight,
      topLeft,
      topRight,
      containerGetBounds,
      calculateNewDimensions,
      applySizeConstraints,
      applyBoundaryConstraints,
      getHandleType,
      rotation,
    ]
  )

  const getBounds = useCallback(() => {
    // Don't constrain the handle movement itself - let it move freely
    // The resize constraints are handled in handleResize
    return {
      min: { x: -Infinity, y: -Infinity },
      max: { x: Infinity, y: Infinity },
      minWidth: 0,
      minHeight: 0,
    }
  }, [])

  return (
    <BaseDragHandle
      {...props}
      css={baseDragHandleCss}
      cursor={`url("data:image/svg+xml;utf8,${encodeURIComponent(
        getRotatedResizeCursor(rotation, !!bottomRight, !!bottomLeft, !!topRight, !!topLeft)
      )}") 12 12, auto`}
      getBounds={getBounds}
      keyboardDescription={getHandleLabel()}
      onDragMove={(delta, currentRect) => handleResize({ currentRect, delta })}
      style={{
        ...(bottomLeft && { bottom: HANDLE_SIZE / -2, left: HANDLE_SIZE / -2 }),
        ...(bottomRight && { bottom: HANDLE_SIZE / -2, right: HANDLE_SIZE / -2 }),
        ...(topLeft && { top: HANDLE_SIZE / -2, left: HANDLE_SIZE / -2 }),
        ...(topRight && { top: HANDLE_SIZE / -2, right: HANDLE_SIZE / -2 }),
        ...props.style,
      }}
      tabIndex={0}
      role="slider"
      aria-valuenow={percentageValue}
      aria-valuemin={50}
      aria-valuemax={Math.round((maxSize / defaultSize) * 100)}
      aria-valuetext={t('AspectRatioResizeHandle.valueText', '{percentage}% of original size', {
        percentage: percentageValue,
      })}
      data-move-handle
    />
  )
}

AspectRatioResizeHandle.propTypes = {
  aspectRatio: PropTypes.number,
  bottomLeft: PropTypes.bool,
  bottomRight: PropTypes.bool,
  getBounds: PropTypes.func,
  maxSize: PropTypes.number,
  minSize: PropTypes.number,
  topLeft: PropTypes.bool,
  topRight: PropTypes.bool,
  value: PropTypes.number,
}
